import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router'

import { env } from '@/app/config/env'
import { requireAiUploader, requireAuth, requireSite } from './guards'

/** Brand suffix appended to every page's <title>. */
const TITLE_SUFFIX = 'EZ-QR'

const routes: RouteRecordRaw[] = [
  {
    path: '/',
    redirect: { name: 'sites' },
  },
  {
    path: '/sites',
    name: 'sites',
    component: () => import('@/features/sites/views/SitesView.vue'),
    meta: { requiresAuth: true, title: 'Sites' },
  },
  {
    path: '/site/:siteId(\\d+)',
    name: 'site-shell',
    component: () => import('@/app/layouts/AppShell.vue'),
    meta: { requiresAuth: true, requiresSite: true },
    children: [
      {
        path: '',
        name: 'site',
        component: () => import('@/features/sites/views/SiteHomeView.vue'),
        meta: { title: 'Dashboard' },
      },
      {
        path: 'dishes',
        name: 'dishes',
        component: () => import('@/features/dishes/views/DishesView.vue'),
        meta: { title: 'Items' },
      },
      {
        path: 'categories',
        name: 'categories',
        component: () => import('@/features/categories/views/CategoriesView.vue'),
        meta: { title: 'Categories' },
      },
      {
        path: 'tags',
        name: 'tags',
        component: () => import('@/features/tags/views/TagsView.vue'),
        meta: { title: 'Tags' },
      },
      {
        path: 'supercategories',
        name: 'supercategories',
        component: () => import('@/features/super-categories/views/SuperCategoriesView.vue'),
        meta: { title: 'Super-categories' },
      },
      {
        path: 'media',
        name: 'media',
        component: () => import('@/features/media/views/MediaLibraryView.vue'),
        meta: { title: 'Media' },
      },
      {
        path: 'shared-modifiers',
        name: 'shared-modifiers',
        component: () => import('@/features/shared-modifiers/views/SharedModifierGroupsView.vue'),
        meta: { title: 'Shared modifiers' },
      },
      {
        path: 'qr',
        name: 'qr',
        component: () => import('@/features/qr/views/QrView.vue'),
        meta: { title: 'QR' },
      },
      // Online ordering: nested layout with sub-pages. Mounted only in
      // non-prod for now — the backend hasn't shipped the settings
      // endpoints yet, so the section is preview-only. Pushed below
      // (gated by `!env.isProd`) so the prod bundle doesn't carry the
      // ordering chunks at all.
    ],
  },
  {
    path: '/auth/login',
    name: 'login',
    component: () => import('@/features/auth/views/LoginView.vue'),
    meta: { publicOnly: true, title: 'Sign in' },
  },
  {
    path: '/auth/access',
    name: 'access-denied',
    component: () => import('@/features/auth/views/AccessDeniedView.vue'),
    meta: { title: 'Access denied' },
  },
]

/**
 * Append route records under the `site-shell` parent. A feature that
 * adds a per-site screen calls this once instead of poking
 * `routes.find(...).children.push(...)` and re-discovering the parent
 * every time — and the find-or-warn surface is in one place if the
 * parent ever moves or its name changes.
 */
function addSiteChildRoutes(...children: RouteRecordRaw[]): void {
  const siteShell = routes.find((r) => r.name === 'site-shell')
  if (!siteShell?.children) {
    // Should be unreachable — the site-shell record is declared at the
    // top of this file. Warn loudly rather than silently drop the
    // routes, which would surface as 404s in the wild.
    console.warn(
      '[router] site-shell route record missing children; dropping',
      children.map((c) => c.name ?? c.path),
    )
    return
  }
  siteShell.children.push(...children)
}

// UI gallery — visible in local/dev only. Prod builds simply won't have
// the route, so the gallery's JS is also dropped from the bundle.
if (!env.isProd) {
  routes.push({
    path: '/_ui',
    name: 'ui-gallery',
    component: () => import('@/app/views/UiGalleryView.vue'),
    meta: { title: 'UI gallery' },
  })
}

/**
 * Online ordering — backed by ezqr/v1/site capabilities + ez-clover
 * for checkout. Always mounted; the view itself handles the
 * missing/disabled/enabled states from the capabilities envelope.
 *
 * Routes hang as children of `site-shell` so they share the AppShell
 * header (site name, switch-site, user menu).
 */
addSiteChildRoutes({
  path: 'ordering',
  component: () => import('@/features/ordering/views/OrderingLayout.vue'),
  children: [
    {
      path: '',
      name: 'site-ordering',
      component: () => import('@/features/ordering/views/OrderingHomeView.vue'),
      meta: { title: 'Online ordering' },
    },
    // Future sub-pages slot in here — `site-ordering-hours`,
    // `-delivery`, `-payments`, etc. Each is a separate
    // lazy-loaded chunk under `features/ordering/views/`.
  ],
})

/**
 * AI menu-photo uploader — dev-only seam.
 *
 * Single placeholder route under `Menu`; the `ezh/v1/*` backend
 * contract isn't shared with the SPA yet, so this is a layout-only
 * stub for the dashboard tile to link to. Wrapped in `!env.isProd`
 * so the prod bundle drops the chunk entirely.
 *
 * Same gating pattern as `Online ordering`: per-site
 * `features.aiUploader.active` is the eventual switch — uncomment
 * the route-meta `requiresAiUploader` line once `/auth/me` ships
 * the flag.
 */
if (!env.isProd) {
  addSiteChildRoutes({
    path: 'ai-uploader',
    name: 'site-ai-uploader',
    component: () => import('@/features/ai-uploader/views/AiUploaderHomeView.vue'),
    // meta: { requiresAiUploader: true, title: 'AI uploader' },
    meta: { title: 'AI uploader' },
  })
}

// Catch-all: matches anything unmatched above. Must come last.
routes.push({
  path: '/:pathMatch(.*)*',
  name: 'not-found',
  component: () => import('@/app/views/NotFoundView.vue'),
  meta: { title: 'Not found' },
})

export const router = createRouter({
  history: createWebHistory(),
  routes,
})

router.beforeEach(requireAuth)
router.beforeEach(requireSite)
router.beforeEach(requireAiUploader)

/**
 * Keep the browser tab title in sync with the active route. Without
 * this every tab read "EZ-QR Web Edit" no matter where the user was —
 * impossible to tell tabs apart when juggling multiple sites.
 *
 * Also moves focus to the routed view's main landmark so keyboard and
 * screen-reader users land on the new content instead of staying on
 * the link they just activated.
 */
router.afterEach((to) => {
  const t = (to.meta?.title as string | undefined) ?? null
  document.title = t ? `${t} · ${TITLE_SUFFIX}` : TITLE_SUFFIX

  // Defer to next tick so the new view is mounted before we look for it.
  if (typeof document !== 'undefined') {
    queueMicrotask(() => {
      const target = document.querySelector<HTMLElement>('main, [role="main"]')
      if (!target) return
      // tabindex=-1 makes a non-interactive landmark focusable without
      // putting it in the tab order; { preventScroll } keeps the user's
      // viewport position (no jump on route change).
      const hadTabindex = target.hasAttribute('tabindex')
      if (!hadTabindex) target.setAttribute('tabindex', '-1')
      target.focus({ preventScroll: true })
      if (!hadTabindex) {
        // Remove on blur so the focus-visible ring doesn't linger.
        target.addEventListener('blur', () => target.removeAttribute('tabindex'), { once: true })
      }
    })
  }
})
